import './EntityOverviewForm.scss';
import cn from 'classnames';
import { useState } from 'react';
import { Form } from 'react-bootstrap';
import { MdEdit, MdSave } from 'react-icons/md';
import { Button } from '@Components/Button';
import { Checkbox } from '@Components/FormCheck';
import { Design, Size } from '@Components/designConstants';
import { Dropdown } from '@Components/Dropdown';
import {
  FieldValue,
  OverviewField,
  OverviewFieldType,
  OverviewSection
} from './entity-overview-form-constants';
import { displayValue, selectOptions } from './entity-overview-form-utils';

type FieldRecord = Record<string, FieldValue>;

interface EntityOverviewFormProps<T> {
  entity: T;
  sections: OverviewSection[];
  saving: boolean;
  onSave: (updated: T) => Promise<boolean>;
  editLabel?: string;
}

// Self-contained: owns its own editing/draft/save cycle. For an entity whose edit/save spans
// multiple sibling tabs sharing one draft (e.g. a whole-record save covering several tab
// components at once), use `OverviewFieldsRenderer` directly instead and lift that state up.
export const EntityOverviewForm = <T extends object>({
  entity,
  sections,
  saving,
  onSave,
  editLabel = 'Edit metadata'
}: EntityOverviewFormProps<T>) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<FieldRecord>({});

  const startEditing = () => {
    setDraft({ ...(entity as FieldRecord) });
    setEditing(true);
  };

  const setField = (field: string, value: FieldValue) =>
    setDraft(prev => ({ ...prev, [field]: value }));

  const handleSave = async () => {
    const saved = await onSave({ ...entity, ...draft });
    if (saved) {
      setEditing(false);
    }
  };

  const source: FieldRecord = editing ? draft : (entity as FieldRecord);

  return (
    <div className='entity-overview-form'>
      <div className='eof-head'>
        {editing ? (
          <>
            <Button
              size={Size.LG}
              design={Design.NEUTRAL}
              disabled={saving}
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
            <Button
              size={Size.LG}
              loading={saving}
              leadingElement={<MdSave />}
              onClick={handleSave}
            >
              Save changes
            </Button>
          </>
        ) : (
          <Button
            size={Size.LG}
            design={Design.NEUTRAL}
            leadingElement={<MdEdit />}
            onClick={startEditing}
          >
            {editLabel}
          </Button>
        )}
      </div>
      <OverviewFieldsRenderer
        sections={sections}
        source={source}
        editing={editing}
        onFieldChange={setField}
      />
    </div>
  );
};

interface OverviewFieldsRendererProps {
  sections: OverviewSection[];
  source: FieldRecord;
  editing: boolean;
  onFieldChange: (field: string, value: FieldValue) => void;
}

// The pure rendering half of the form — grouped sections of field controls, with no opinion on who
// owns the editing/draft state. `EntityOverviewForm` wraps this with its own self-managed state; a
// caller whose save spans multiple sibling components (one shared draft/edit toggle across several
// tabs) can render this directly against lifted state instead.
export const OverviewFieldsRenderer = ({
  sections,
  source,
  editing,
  onFieldChange
}: OverviewFieldsRendererProps) => (
  <div className='eof-fields'>
    {sections.map(section => (
      <div key={section.title} className='eof-section'>
        <div className='eof-section-title'>{section.title}</div>
        <div className='eof-grid'>
          {section.fields.map(field => (
            <OverviewFieldControl
              key={field.field}
              field={field}
              value={source[field.field]}
              editing={editing}
              onChange={value => onFieldChange(field.field, value)}
            />
          ))}
        </div>
      </div>
    ))}
  </div>
);

interface OverviewFieldControlProps {
  field: OverviewField;
  value: FieldValue;
  editing: boolean;
  onChange: (value: FieldValue) => void;
}

const OverviewFieldControl = ({ field, value, editing, onChange }: OverviewFieldControlProps) => {
  const readOnly = !editing || field.readOnly;
  const isTextarea = field.type === OverviewFieldType.TEXTAREA;
  const className = cn('eof-field', { 'eof-field-full': field.fullWidth });

  if (readOnly) {
    return (
      <div className={className}>
        <span className='eof-label'>{field.label}</span>
        {isTextarea ? (
          <p className='eof-value-multiline'>{displayValue(field, value)}</p>
        ) : (
          <span className='eof-value'>
            {field.type === OverviewFieldType.COLOR && value && (
              <span className='eof-swatch' style={{ backgroundColor: String(value) }} />
            )}
            {displayValue(field, value)}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className={className}>
      <span className='eof-label'>{field.label}</span>
      <FieldEditor field={field} value={value} onChange={onChange} />
    </div>
  );
};

interface FieldEditorProps {
  field: OverviewField;
  value: FieldValue;
  onChange: (value: FieldValue) => void;
}

const FieldEditor = ({ field, value, onChange }: FieldEditorProps) => {
  if (field.type === OverviewFieldType.CHECKBOX) {
    const isChecked = field.toChecked ? field.toChecked(value) : Boolean(value);
    return (
      <Checkbox
        checked={isChecked}
        onChange={event =>
          onChange(
            field.fromChecked ? field.fromChecked(event.target.checked) : event.target.checked
          )
        }
      />
    );
  }

  if (field.type === OverviewFieldType.SELECT) {
    const options = selectOptions(field.options);
    if (field.creatable) {
      return (
        <Dropdown<string>
          searchable
          creatable
          options={options}
          value={value == null ? null : String(value)}
          placeholder='Select or create…'
          clearable
          onChange={selected => onChange(selected)}
          onClear={() => onChange(null)}
        />
      );
    }
    return (
      <Dropdown<string>
        options={options}
        value={String(value ?? '')}
        clearable
        onChange={selected => onChange(selected)}
        onClear={() => onChange(null)}
      />
    );
  }

  if (field.type === OverviewFieldType.TEXTAREA) {
    return (
      <Form.Control
        as='textarea'
        size='sm'
        rows={4}
        value={value == null ? '' : String(value)}
        onChange={event => onChange(event.target.value)}
      />
    );
  }

  return (
    <Form.Control
      size='sm'
      value={value == null ? '' : String(value)}
      onChange={event => onChange(event.target.value)}
    />
  );
};
